/**
 * Medidor de cuota: consumido contra un tope, con proyección a fin de periodo.
 *
 * Presentacional a propósito — no consulta nada. La idea es que las tres
 * cuotas de AROC (tokens de análisis, tráfico de salida, costo) se vean
 * IGUALES: misma barra, mismos tres estados, misma marca de proyección.
 * Pintarlas distinto hace que se lean como métricas sin relación.
 *
 * `scope` no es decoración: el tope de tokens se calcula sobre
 * `analysis_sessions` y los totales de "Uso global" sobre `llm_usage`.
 * Son fuentes distintas y pueden no coincidir; rotular el alcance evita
 * que la diferencia se lea como un error de suma.
 */

const VERDE = 70;
const AMBAR = 90;

export interface QuotaMeterProps {
  title: string;
  /** Qué abarca la medición. Se pinta bajo el título. */
  scope: string;
  used: number;
  limit: number;
  /** Formateador de `used`/`limit` (tokens, bytes, dólares…). */
  format: (n: number) => string;
  /** Proyección a fin de periodo. Se dibuja como marca, no como relleno. */
  projected?: number | null;
  /** Texto del próximo reinicio, ya formateado. */
  resetsLabel?: string | null;
  /** Sin tope configurado: se pinta el consumo pero no hay barra que llenar. */
  disabled?: boolean;
}

function estado(pct: number): { fill: string; texto: string; etiqueta: string } {
  if (pct >= AMBAR) {
    return { fill: "bg-red-600", texto: "text-red-600", etiqueta: "Crítico" };
  }
  if (pct >= VERDE) {
    return { fill: "bg-amber-500", texto: "text-amber-600", etiqueta: "Atención" };
  }
  return { fill: "bg-emerald-600", texto: "text-emerald-600", etiqueta: "Dentro de cuota" };
}

export function QuotaMeter({
  title,
  scope,
  used,
  limit,
  format,
  projected = null,
  resetsLabel = null,
  disabled = false,
}: QuotaMeterProps) {
  const pct = limit > 0 ? (100 * used) / limit : 0;
  const pctProyectado = limit > 0 && projected != null ? (100 * projected) / limit : null;
  // El estado lo manda la proyección cuando existe: llegar al 95% el día 28
  // no es lo mismo que ir en 40% el día 3 rumbo a 400%.
  const { fill, texto, etiqueta } = estado(Math.max(pct, pctProyectado ?? 0));

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-medium">{title}</h2>
        {!disabled && (
          <span className={`text-xs font-medium ${texto}`}>{etiqueta}</span>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">{scope}</p>

      {disabled ? (
        <p className="text-sm text-muted-foreground mt-3">
          Sin tope configurado. Consumido este periodo: {format(used)}.
        </p>
      ) : (
        <>
          <div className="flex items-baseline gap-2 mt-3">
            <span className="text-2xl font-semibold tabular-nums">
              {pct.toLocaleString("es-MX", { maximumFractionDigits: 2 })}%
            </span>
            <span className="text-sm text-muted-foreground tabular-nums">
              {format(used)} de {format(limit)}
            </span>
          </div>

          <div
            className="relative h-3 mt-3 rounded-full bg-muted overflow-hidden"
            role="progressbar"
            aria-label={title}
            aria-valuenow={Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={`absolute inset-y-0 left-0 ${fill}`}
              style={{ width: `${Math.min(100, Math.max(pct, used > 0 ? 0.5 : 0))}%` }}
            />
            {pctProyectado != null && pctProyectado > pct && (
              <div
                className="absolute inset-y-0 w-0.5 bg-foreground/60"
                style={{ left: `${Math.min(100, pctProyectado)}%` }}
                aria-hidden="true"
              />
            )}
          </div>

          <div className="flex flex-wrap justify-between gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground tabular-nums">
            {pctProyectado != null ? (
              <span>
                Proyección a fin de periodo: {format(Math.round(projected as number))} (
                {pctProyectado.toLocaleString("es-MX", { maximumFractionDigits: 0 })}%)
              </span>
            ) : (
              <span />
            )}
            {resetsLabel && <span>Reinicia {resetsLabel}</span>}
          </div>
        </>
      )}
    </div>
  );
}
